<script setup>
import { useAlert } from 'dashboard/composables';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import EmailTemplatesAPI from 'dashboard/api/emailTemplates';
import { computed, nextTick, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

defineOptions({
  name: 'EmailTemplateEdit',
});

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const isLoading = ref(true);
const isSaving = ref(false);
const isResetting = ref(false);
const isSendingTest = ref(false); // REMOVER DEPOIS DE TESTAR
const template = ref(null);
const body = ref('');
const bodyTextarea = ref(null);
const selectionStart = ref(null);
const selectionEnd = ref(null);

const templateId = computed(() => route.params.templateId);
const canEdit = computed(() => template.value?.editable);
const variables = computed(() => template.value?.variables || []);

const rememberSelection = event => {
  selectionStart.value = event.target.selectionStart ?? body.value.length;
  selectionEnd.value = event.target.selectionEnd ?? body.value.length;
};

const rememberSelectionFromTextarea = () => {
  const textarea = bodyTextarea.value;
  if (!textarea || !canEdit.value) return;

  selectionStart.value = textarea.selectionStart ?? body.value.length;
  selectionEnd.value = textarea.selectionEnd ?? body.value.length;
};

const insertVariable = async variableName => {
  if (!canEdit.value) return;

  const token = `{{ ${variableName} }}`;
  const current = body.value || '';
  const start =
    selectionStart.value === null
      ? current.length
      : Math.min(selectionStart.value, current.length);
  const end =
    selectionEnd.value === null
      ? current.length
      : Math.min(selectionEnd.value, current.length);

  body.value = `${current.slice(0, start)}${token}${current.slice(end)}`;

  const cursor = start + token.length;
  selectionStart.value = cursor;
  selectionEnd.value = cursor;

  await nextTick();
  const textarea = bodyTextarea.value;
  if (!textarea) return;

  textarea.focus();
  textarea.setSelectionRange(cursor, cursor);
  textarea.scrollTop = textarea.scrollHeight;
};

const fetchTemplate = async () => {
  isLoading.value = true;
  try {
    const { data } = await EmailTemplatesAPI.show(templateId.value);
    template.value = data;
    body.value = data.body || '';
  } catch {
    useAlert(t('EMAIL_TEMPLATES.API.ERROR_MESSAGE'));
    router.push({ name: 'email_templates_list' });
  } finally {
    isLoading.value = false;
  }
};

const saveTemplate = async () => {
  if (!canEdit.value) return;

  isSaving.value = true;
  try {
    const { data } = await EmailTemplatesAPI.update(templateId.value, {
      email_template: { body: body.value },
    });
    template.value = data;
    useAlert(t('EMAIL_TEMPLATES.EDIT.API.SUCCESS_MESSAGE'));
  } catch {
    useAlert(t('EMAIL_TEMPLATES.EDIT.API.ERROR_MESSAGE'));
  } finally {
    isSaving.value = false;
  }
};

const resetTemplate = async () => {
  if (!canEdit.value) return;
  if (!window.confirm(t('EMAIL_TEMPLATES.EDIT.RESET_CONFIRM'))) return;

  isResetting.value = true;
  try {
    const { data } = await EmailTemplatesAPI.reset(templateId.value);
    template.value = data;
    body.value = data.body || '';
    useAlert(t('EMAIL_TEMPLATES.EDIT.RESET_SUCCESS'));
  } catch {
    useAlert(t('EMAIL_TEMPLATES.EDIT.API.ERROR_MESSAGE'));
  } finally {
    isResetting.value = false;
  }
};

// REMOVE AFTER QA — TmpEmailTemplateTest
const sendTestEmail = async () => {
  isSendingTest.value = true;
  try {
    const payload = canEdit.value ? { body: body.value } : {};
    const { data } = await EmailTemplatesAPI.sendTest(templateId.value, payload);
    if (canEdit.value && data) {
      const show = await EmailTemplatesAPI.show(templateId.value);
      template.value = show.data;
    }
    useAlert(data.message || 'E-mail de teste enviado.');
  } catch (error) {
    const message =
      error?.response?.data?.error || 'Falha ao enviar e-mail de teste.';
    useAlert(message);
  } finally {
    isSendingTest.value = false;
  }
};

onMounted(fetchTemplate);
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="$t('EMAIL_TEMPLATES.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="template?.label || $t('EMAIL_TEMPLATES.EDIT.TITLE')"
        :description="template?.group || ''"
        :back-button-label="$t('EMAIL_TEMPLATES.EDIT.BACK')"
      >
        <template #actions>
          <!-- REMOVE AFTER QA — TmpEmailTemplateTest -->
          <Button
            faded
            slate
            label="Enviar teste"
            :is-loading="isSendingTest"
            @click="sendTestEmail"
          />
          <Button
            v-if="canEdit"
            faded
            :label="$t('EMAIL_TEMPLATES.EDIT.RESET')"
            :is-loading="isResetting"
            @click="resetTemplate"
          />
          <Button
            v-if="canEdit"
            :label="$t('EMAIL_TEMPLATES.EDIT.SAVE')"
            :is-loading="isSaving"
            @click="saveTemplate"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <div v-if="template" class="grid w-full gap-6 lg:grid-cols-3">
        <div class="flex flex-col gap-3 lg:col-span-2">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('EMAIL_TEMPLATES.EDIT.BODY_LABEL') }}
          </label>
          <textarea
            ref="bodyTextarea"
            v-model="body"
            rows="24"
            class="w-full px-3 py-2 font-mono text-sm border rounded-lg border-n-weak bg-n-solid-1 text-n-slate-12"
            :readonly="!canEdit"
            :placeholder="$t('EMAIL_TEMPLATES.EDIT.BODY_PLACEHOLDER')"
            @click="rememberSelection"
            @keyup="rememberSelection"
            @select="rememberSelection"
          />
          <p v-if="!canEdit" class="text-sm text-n-slate-10">
            {{ $t('EMAIL_TEMPLATES.EDIT.READ_ONLY_NOTE') }}
          </p>
        </div>

        <div class="flex flex-col gap-3">
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ $t('EMAIL_TEMPLATES.EDIT.VARIABLES_TITLE') }}
          </h3>
          <p class="text-xs text-n-slate-11">
            {{
              canEdit
                ? $t('EMAIL_TEMPLATES.EDIT.VARIABLES_DESCRIPTION')
                : $t('EMAIL_TEMPLATES.EDIT.VARIABLES_READ_ONLY')
            }}
          </p>
          <div class="flex flex-col gap-2 max-h-[32rem] overflow-y-auto">
            <button
              v-for="variable in variables"
              :key="variable.name"
              type="button"
              class="flex flex-col items-start w-full px-3 py-2 text-left border rounded-lg border-n-weak"
              :class="
                canEdit
                  ? 'hover:bg-n-alpha-1 cursor-pointer'
                  : 'opacity-70 cursor-default'
              "
              :disabled="!canEdit"
              @mousedown.prevent="rememberSelectionFromTextarea"
              @click="insertVariable(variable.name)"
            >
              <code class="text-xs text-n-brand">{{ variable.name }}</code>
              <span class="text-xs text-n-slate-11">{{ variable.description }}</span>
            </button>
          </div>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
